import React, { useEffect, useState } from 'react';
import { openInAppFilePreview } from '../../utils/filePreviewBus';
import { labelValidasi } from '../../utils/labelValidasi';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, MenuItem,
  LinearProgress, Paper, Stack, TextField, Typography, IconButton, Tooltip,
  Divider, Grid, useMediaQuery
} from '@mui/material';
import {
  Assignment, CheckCircle, HomeWork, Phone, Refresh, ReportProblem,
  Receipt, Engineering, Payments, PendingActions, TaskAlt, Person
} from '@mui/icons-material';
import api from '../../api';
import { toProtectedFileUrl } from '../../utils/secureFile';

// Palette netral flat — mengikuti gaya dashboard monitoring (tanpa gradien/warna mencolok)
const token = {
  bg: '#f5f7fa',
  surface: '#ffffff',
  ink: '#1f2937',
  inkSoft: '#6b7280',
  line: '#e5e7eb',
  primary: '#1976d2',
  success: '#2e7d32',
  warning: '#ed6c02',
  info: '#0288d1',
  error: '#d32f2f',
};

const currency = new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 });
const formatDate = (value) => value ? new Date(value).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' }) : '-';
const formatDateTime = (value) => value ? new Date(value).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
const statusColor = (status) =>
  status === 'Selesai' ? 'success'
    : status === 'Sedang Berlangsung' || status === 'Proses' || status === 'Dalam Proses' ? 'info'
    : status === 'Menunggu Verifikasi' ? 'warning'
    : 'default';
const waLink = (phone) => phone ? `https://wa.me/${String(phone).replace(/\D/g, '').replace(/^0/, '62')}` : null;

const apiFile = (url) => {
  if (!url) return null;
  if (String(url).startsWith('http') || String(url).startsWith('/files/')) return toProtectedFileUrl(url);
  return toProtectedFileUrl(String(url).startsWith('/') ? url : `/${url}`);
};

const StatCard = ({ icon, label, value, color = 'primary', sub }) => (
  <Paper sx={{ p: 2, bgcolor: token.surface, border: `1px solid ${token.line}`, height: '100%' }} elevation={0}>
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box sx={{
        width: 44, height: 44, borderRadius: 2, display: 'grid', placeItems: 'center',
        bgcolor: `${token[color]}14`, color: token[color], flexShrink: 0,
      }}>
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h6" fontWeight={700} sx={{ color: token.ink, lineHeight: 1.2, wordBreak: 'break-word' }}>{value}</Typography>
        <Typography variant="caption" sx={{ color: token.inkSoft }}>{label}</Typography>
        {sub && <Typography variant="caption" display="block" sx={{ color: token.inkSoft }}>{sub}</Typography>}
      </Box>
    </Stack>
  </Paper>
);

const ProgressProsesCard = ({ label, icon, pct, color = 'primary' }) => (
  <Paper sx={{ p: 2, bgcolor: token.surface, border: `1px solid ${token.line}`, height: '100%' }} elevation={0}>
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
      {icon}
      <Typography variant="body2" fontWeight={600} sx={{ color: token.ink }}>{label}</Typography>
    </Stack>
    <Typography variant="h5" fontWeight={700} sx={{ color: token[color] }}>{pct}%</Typography>
    <LinearProgress variant="determinate" value={Math.min(100, pct)} color={color}
      sx={{ height: 6, borderRadius: 3, mt: 1, bgcolor: token.line }} />
    <Typography variant="caption" sx={{ color: token.inkSoft, mt: 0.5, display: 'block' }}>
      {pct.completedSteps ?? 0}/{pct.totalSteps ?? 0} langkah selesai
    </Typography>
  </Paper>
);

const StepRow = ({ step, index, isLast }) => (
  <Box sx={{ display: 'flex', gap: 1.5, position: 'relative' }}>
    {!isLast && <Box sx={{ position: 'absolute', left: 11, top: 26, bottom: -6, width: 2, bgcolor: token.line }} />}
    <Box sx={{
      width: 24, height: 24, borderRadius: '50%', flexShrink: 0, mt: 0.3, zIndex: 1,
      display: 'grid', placeItems: 'center',
      bgcolor: step.status === 'Selesai' ? token.success : step.status === 'Sedang Berlangsung' ? token.warning : token.surface,
      border: `2px solid ${step.status === 'Belum Dimulai' ? token.line : 'transparent'}`,
      color: '#fff',
    }}>
      {step.status === 'Selesai' ? <CheckCircle sx={{ fontSize: 14 }} /> : null}
    </Box>
    <Box sx={{ flex: 1, pb: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={0.5} alignItems={{ sm: 'center' }}>
        <Typography variant="body2" fontWeight={600} sx={{ color: token.ink }}>
          {step.order}. {step.title}
        </Typography>
        <Chip size="small" label={labelValidasi(step.status)} color={statusColor(step.status)} />
      </Stack>
      <Typography variant="caption" sx={{ color: token.inkSoft }}>
        Selesai: {formatDate(step.completedAt)}
      </Typography>
      {[...(step.photos || []), ...(step.documents || [])].length > 0 && (
        <Stack direction="row" spacing={1} mt={1} sx={{ overflowX: 'auto' }}>
          {[...(step.photos || []), ...(step.documents || [])].map((file, i) => (
            <Button key={`${file.name}-${i}`} size="small" variant="outlined" sx={{ flexShrink: 0, textTransform: 'none' }}
              onClick={() => openInAppFilePreview({ url: apiFile(file.url), title: file.name || 'Dokumen' })}>
              {/\.(jpg|jpeg|png|webp|gif)$/i.test(file.name || '') ? 'Foto' : (file.name || 'Dokumen')}
            </Button>
          ))}
        </Stack>
      )}
    </Box>
  </Box>
);

const SectionSteps = ({ title, icon, section }) => (
  <Card elevation={0} sx={{ border: `1px solid ${token.line}`, borderRadius: 2 }}>
    <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          {icon}
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: token.ink }}>{title}</Typography>
        </Stack>
        <Chip size="small" variant="outlined" label={`${Math.round(section.percentage)}%`} />
      </Stack>
      {section.steps.length === 0 ? (
        <Typography variant="body2" sx={{ color: token.inkSoft }}>Belum ada tahapan.</Typography>
      ) : (
        section.steps.map((step, idx) => (
          <StepRow key={step.id} step={step} index={idx} isLast={idx === section.steps.length - 1} />
        ))
      )}
    </CardContent>
  </Card>
);

const PenerimaDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [complaintOpen, setComplaintOpen] = useState(false);
  const [complaint, setComplaint] = useState({ BantuanLangkahId: '', keluhan: '', bukti: null });
  const [eligibility, setEligibility] = useState({ canCreate: true });
  const [saving, setSaving] = useState(false);
  const isMobile = useMediaQuery('(max-width:800px)');

  const load = async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    setError('');
    try {
      const response = await api.get('/penerima/dashboard');
      setData(response.data.data);
      const eligibilityResponse = await api.get('/keluhan-eligibility');
      setEligibility(eligibilityResponse.data);
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Dashboard tidak dapat dimuat.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submitComplaint = async () => {
    if (!complaint.BantuanLangkahId || !complaint.keluhan.trim()) return;
    const formData = new FormData();
    formData.append('BantuanLangkahId', complaint.BantuanLangkahId);
    formData.append('keluhan', complaint.keluhan.trim());
    if (complaint.bukti) formData.append('bukti', complaint.bukti);
    setSaving(true);
    try {
      await api.post('/keluhan', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setComplaintOpen(false);
      setComplaint({ BantuanLangkahId: '', keluhan: '', bukti: null });
      await load(true);
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Keluhan gagal dikirim.');
    } finally { setSaving(false); }
  };

  const contentStyle = isMobile ? {} : { marginLeft: '250px', marginRight: '20px' };

  if (loading) return (
    <Box sx={{ minHeight: 420, display: 'grid', placeItems: 'center', bgcolor: token.bg }}>
      <CircularProgress />
    </Box>
  );
  if (error && !data) return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: token.bg, minHeight: '100vh' }}>
      <Alert severity="error" action={<Button color="inherit" onClick={() => load()}>Coba Lagi</Button>}>{error}</Alert>
    </Box>
  );

  const { assistance, progress, steps, contacts, complaints, monitoring } = data;

  return (
    <div className="main-content" style={contentStyle}>
      <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: token.bg, minHeight: '100vh' }}>
        {/* Header — flat & netral */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="h5" fontWeight={600} sx={{ color: token.ink }}>
              <HomeWork sx={{ mr: 1, verticalAlign: 'middle', color: token.primary }} />
              Dashboard Penerima Bantuan
            </Typography>
            <Typography variant="body2" sx={{ color: token.inkSoft }}>
              {assistance.recipientName || 'Penerima Bantuan'} — {assistance.assistanceType}
            </Typography>
          </Box>
          <Tooltip title="Perbarui data">
            <span>
              <IconButton onClick={() => load(true)} disabled={refreshing} size="small"
                sx={{ border: `1px solid ${token.line}`, bgcolor: token.surface }}>
                <Refresh fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

        {/* Statistik ringkas */}
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={6} md={3}>
            <StatCard icon={<HomeWork />} label="Nilai Bantuan" value={currency.format(assistance.amount || 0)} color="primary"
              sub={`${assistance.village || '-'}, ${assistance.district || '-'}`} />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard icon={<Assignment />} label="Status Proses" value={progress.status || '-'} color="info"
              sub={assistance.contractStatus || 'Status kontrak belum tersedia'} />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard icon={<Receipt />} label="Dokumen SK" value={assistance.skNumber || '-'} color="warning"
              sub={`Tanggal SK: ${formatDate(assistance.skDate)}`} />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard icon={<TaskAlt />} label="Progres Bantuan" value={`${Math.round(progress.percentage || 0)}%`} color="success"
              sub={`${progress.completedSteps}/${progress.totalSteps} tahapan selesai`} />
          </Grid>
        </Grid>

        {/* Progress per proses */}
        <Typography variant="subtitle2" fontWeight={600} sx={{ color: token.ink, mb: 1 }}>Progress per Proses</Typography>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={3}>
            <ProgressProsesCard label="Fisik" icon={<Engineering fontSize="small" sx={{ color: token.success }} />} pct={monitoring.physical.percentage} color="success" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <ProgressProsesCard label="Administrasi" icon={<Receipt fontSize="small" sx={{ color: token.info }} />} pct={monitoring.administrative.percentage} color="info" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <ProgressProsesCard label="Keuangan" icon={<Payments fontSize="small" sx={{ color: token.warning }} />} pct={monitoring.financial.percentage} color="warning" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, bgcolor: token.surface, border: `1px solid ${token.line}`, height: '100%' }} elevation={0}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <HomeWork fontSize="small" sx={{ color: token.primary }} />
                <Typography variant="body2" fontWeight={600} sx={{ color: token.ink }}>Material & Upah</Typography>
              </Stack>
              <Typography variant="body2" fontWeight={700} sx={{ color: token.success }}>{currency.format(monitoring.materials.totalSpent)}</Typography>
              <Typography variant="caption" sx={{ color: token.inkSoft, display: 'block' }}>Material terbeli</Typography>
              <Typography variant="body2" fontWeight={700} sx={{ color: token.warning, mt: 0.5 }}>{currency.format(monitoring.wages.totalPaid)}</Typography>
              <Typography variant="caption" sx={{ color: token.inkSoft, display: 'block' }}>Upah terbayar</Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Timeline progres bantuan */}
        <Card elevation={0} sx={{ border: `1px solid ${token.line}`, borderRadius: 2, mb: 2 }}>
          <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: token.ink }}>Tahapan Bantuan</Typography>
              <Chip size="small" variant="outlined" label={`${progress.completedSteps}/${progress.totalSteps} selesai`} />
            </Stack>
            {steps.map((step, idx) => (
              <StepRow key={step.id} step={step} index={idx} isLast={idx === steps.length - 1} />
            ))}
          </CardContent>
        </Card>

        <Grid container spacing={2}>
          {/* Pendamping */}
          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={{ border: `1px solid ${token.line}`, borderRadius: 2, height: '100%' }}>
              <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: token.ink, mb: 1.5 }}>
                  <Person sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />Pendamping Anda
                </Typography>
                {[['TFL', contacts.tfl], ['AGR', contacts.agr]].map(([label, contact]) => (
                  <Paper key={label} variant="outlined" sx={{ p: 1.5, mb: 1.5, borderRadius: 2, borderColor: token.line, '&:last-child': { mb: 0 } }}>
                    <Typography variant="caption" sx={{ color: token.inkSoft }}>{label}</Typography>
                    <Typography variant="body2" fontWeight={700} sx={{ color: token.ink }}>{contact?.name || 'Belum ditentukan'}</Typography>
                    {contact?.whatsapp && (
                      <Button href={waLink(contact.whatsapp)} target="_blank" size="small" startIcon={<Phone />}
                        sx={{ mt: 0.5, textTransform: 'none', color: token.success, fontWeight: 600, pl: 0 }}>
                        Hubungi WhatsApp
                      </Button>
                    )}
                  </Paper>
                ))}
              </CardContent>
            </Card>
          </Grid>

          {/* Keluhan */}
          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={{ border: `1px solid ${token.line}`, borderRadius: 2, height: '100%' }}>
              <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ color: token.ink }}>
                    <ReportProblem sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20, color: token.warning }} />Keluhan Saya
                  </Typography>
                  <Button size="small" variant="contained" disabled={!eligibility.canCreate}
                    onClick={() => setComplaintOpen(true)} sx={{ textTransform: 'none' }}>
                    Buat Laporan
                  </Button>
                </Stack>

                {!eligibility.canCreate && (
                  <Alert severity="info" sx={{ mb: 1.5 }}>
                    {eligibility.reason === 'UNRESOLVED'
                      ? 'Keluhan sebelumnya belum selesai ditangani.'
                      : `Laporan baru dapat dibuat setelah ${formatDate(eligibility.eligibleAt)}.`}
                  </Alert>
                )}

                {complaints.length === 0 ? (
                  <Typography variant="body2" sx={{ color: token.inkSoft }}>Belum ada laporan keluhan.</Typography>
                ) : complaints.slice(0, 3).map((item) => (
                  <Paper key={item.id} variant="outlined" sx={{ p: 1.5, mb: 1.5, borderRadius: 2, borderColor: token.line, '&:last-child': { mb: 0 } }}>
                    <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
                      <Typography variant="body2" fontWeight={700} sx={{ color: token.ink }}>
                        {item.BantuanLangkahLog?.tahap || 'Tahapan bantuan'}
                      </Typography>
                      <Chip size="small" label={item.status} color={statusColor(item.status)} />
                    </Stack>
                    <Typography variant="body2" sx={{ mt: 0.5, color: token.ink }} noWrap>{item.keluhan}</Typography>
                    <Typography variant="caption" sx={{ color: token.inkSoft }}>
                      Dilaporkan {formatDateTime(item.createdAt)}
                    </Typography>
                  </Paper>
                ))}
                {complaints.length > 0 && (
                  <Button size="small" href="/penerima/tindak-lanjut" sx={{ mt: 1, textTransform: 'none' }}>
                    Lihat monitoring tindak lanjut →
                  </Button>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Detail langkah per proses */}
        <Typography variant="subtitle2" fontWeight={600} sx={{ color: token.ink, mt: 3, mb: 1 }}>Rincian Tahapan</Typography>
        <Stack spacing={2}>
          <SectionSteps title="Progres Pembangunan Fisik" icon={<Engineering fontSize="small" sx={{ color: token.success }} />} section={monitoring.physical} />
          <Grid container spacing={2}>
            <Grid item xs={12} lg={6}>
              <SectionSteps title="Progres Administrasi" icon={<Receipt fontSize="small" sx={{ color: token.info }} />} section={monitoring.administrative} />
            </Grid>
            <Grid item xs={12} lg={6}>
              <SectionSteps title="Progres Keuangan" icon={<Payments fontSize="small" sx={{ color: token.warning }} />} section={monitoring.financial} />
            </Grid>
          </Grid>

          {/* Pembelian material & upah */}
          <Grid container spacing={2}>
            <Grid item xs={12} lg={7}>
              <Card elevation={0} sx={{ border: `1px solid ${token.line}`, borderRadius: 2 }}>
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ color: token.ink, mb: 0.5 }}>Pembelian Material</Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ color: token.success, mb: 1.5 }}>{currency.format(monitoring.materials.totalSpent)}</Typography>
                  {monitoring.materials.purchases.length === 0 ? (
                    <Typography variant="body2" sx={{ color: token.inkSoft }}>Belum ada transaksi material.</Typography>
                  ) : monitoring.materials.purchases.map((purchase) => (
                    <Paper key={purchase.id} variant="outlined" sx={{ p: 1.5, mb: 1.5, borderColor: token.line }}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" fontWeight={700}>{purchase.supplier}</Typography>
                        <Typography variant="body2" fontWeight={700}>{currency.format(purchase.total)}</Typography>
                      </Stack>
                      {purchase.items.map((item) => (
                        <Typography key={item.id} variant="caption" display="block" sx={{ color: token.inkSoft }}>
                          {item.name}: {item.quantity} {item.unit || ''} × {currency.format(item.unitPrice)}
                        </Typography>
                      ))}
                      {purchase.photos.length > 0 && (
                        <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
                          {purchase.photos.map((photo) => (
                            <Button key={photo.label} size="small" variant="outlined" sx={{ textTransform: 'none' }}
                              onClick={() => openInAppFilePreview({ url: apiFile(photo.url), title: photo.label || 'Foto' })}>
                              {photo.label}
                            </Button>
                          ))}
                        </Stack>
                      )}
                    </Paper>
                  ))}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} lg={5}>
              <Card elevation={0} sx={{ border: `1px solid ${token.line}`, borderRadius: 2 }}>
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ color: token.ink, mb: 0.5 }}>Upah Tukang</Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ color: token.warning, mb: 1.5 }}>{currency.format(monitoring.wages.totalPaid)}</Typography>
                  {monitoring.wages.entries.length === 0 ? (
                    <Typography variant="body2" sx={{ color: token.inkSoft }}>Belum ada pembayaran upah.</Typography>
                  ) : monitoring.wages.entries.map((entry) => (
                    <Paper key={entry.id} variant="outlined" sx={{ p: 1.5, mb: 1.5, borderColor: token.line }}>
                      <Typography variant="body2" fontWeight={700}>{entry.workerCount} tukang, {entry.dayCount} hari</Typography>
                      <Typography variant="caption" sx={{ color: token.inkSoft }}>{currency.format(entry.dailyRate)} / hari</Typography>
                      <Typography variant="body2" fontWeight={700}>{currency.format(entry.total)}</Typography>
                      {entry.photos.map((photo) => (
                        <Button key={photo.name} size="small" sx={{ textTransform: 'none' }}
                          onClick={() => openInAppFilePreview({ url: apiFile(photo.url), title: photo.name || 'Kwitansi' })}>
                          Lihat Kwitansi
                        </Button>
                      ))}
                    </Paper>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Stack>
      </Box>

      {/* Dialog buat keluhan */}
      <Dialog open={complaintOpen} onClose={() => !saving && setComplaintOpen(false)}
        maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontWeight: 700 }}>Laporkan Keluhan</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField select required label="Tahapan yang bermasalah" value={complaint.BantuanLangkahId}
              onChange={(event) => setComplaint((v) => ({ ...v, BantuanLangkahId: event.target.value }))}>
              {steps.map((step) => <MenuItem key={step.id} value={step.id}>{step.order}. {step.title}</MenuItem>)}
            </TextField>
            <TextField required multiline rows={4} label="Jelaskan keluhan" value={complaint.keluhan}
              onChange={(event) => setComplaint((v) => ({ ...v, keluhan: event.target.value }))}
              inputProps={{ maxLength: 1000 }} />
            <Button component="label" variant="outlined" sx={{ textTransform: 'none' }}>
              Lampirkan foto (opsional)
              <input hidden type="file" accept="image/jpeg,image/png,image/webp"
                onChange={(event) => setComplaint((v) => ({ ...v, bukti: event.target.files?.[0] || null }))} />
            </Button>
            {complaint.bukti && <Typography variant="caption" sx={{ color: token.inkSoft }}>{complaint.bukti.name}</Typography>}
            <Alert severity="warning">
              Laporan baru tidak dapat dibuat sebelum laporan ini selesai ditangani dan selama 3x24 jam setelah penyelesaian.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={() => setComplaintOpen(false)} sx={{ textTransform: 'none', color: token.inkSoft }}>Batal</Button>
          <Button disabled={saving || !complaint.BantuanLangkahId || !complaint.keluhan.trim()} onClick={submitComplaint} variant="contained" sx={{ textTransform: 'none' }}>
            {saving ? 'Mengirim...' : 'Kirim Laporan'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PenerimaDashboard;
